class UTM {
  constructor() {
    this.url = new URL(window.location.href);
    this.elIframeForms = null;
    this.data = {};
    this.cookieName = 'utm_cookie';
    this.cookieExpireDays = 365;
    this.init();
    this.debug();
  }

  init() {
    this.handleUtmValues();
    
    // Wait for DOM if not ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.handleIframeForms());
    } else {
      this.handleIframeForms();
    }
  }

  /**
   * Handle UTM values from URL and update cookie if needed
   */
  handleUtmValues() {
    if (this.utmValuesExist()) {
      const existingCookie = this.getCookie(this.cookieName);
      
      if (existingCookie) {
        // Compare cookie values properly
        try {
          const existingData = JSON.parse(decodeURIComponent(existingCookie));
          const hasChanged = this.hasDataChanged(existingData, this.data);
          
          if (hasChanged) {
            console.log('UTM values have changed. Updating cookie.');
            this.createCookie();
          }
        } catch (error) {
          console.error('Error parsing existing UTM cookie:', error);
          this.createCookie(); // Create fresh cookie if parsing fails
        }
      } else {
        console.log('Creating new UTM cookie.');
        this.createCookie();
      }
    }
  }

  /**
   * Compare two data objects to detect changes
   */
  hasDataChanged(oldData, newData) {
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    
    for (const key of keys) {
      if (oldData[key] !== newData[key]) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Populate local wufoo iframe form src attributes with utm values
   */
  handleIframeForms() {
    this.elIframeForms = document.querySelectorAll('.utm-form');
    
    if (!this.elIframeForms || this.elIframeForms.length === 0) {
      return;
    }

    // Modify the src attribute of each local wufoo iframe
    this.elIframeForms.forEach(iframeForm => {
      if (!iframeForm.dataset.src) {
        console.warn('UTM form missing data-src attribute:', iframeForm);
        return;
      }
      
      const separator = iframeForm.dataset.src.includes('?') ? '&' : '?';
      iframeForm.dataset.src = iframeForm.dataset.src + 
        separator + 
        UTM.getUTMValueString() + 
        '&ver=' + Date.now();

      // Initialize LazyLoad if available
      if (typeof LazyLoad !== 'undefined') {
        new LazyLoad({
          elements_selector: ".utm-form",
        });
      }
    });
  }

  /**
   * Create cookie with UTM data
   */
  createCookie() {
    const date = new Date();
    date.setTime(date.getTime() + (this.cookieExpireDays * 86400000));
    
    const cookieValue = encodeURIComponent(JSON.stringify(this.data));
    const cookieString = `${this.cookieName}=${cookieValue}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
    
    document.cookie = cookieString;
    console.log('UTM cookie created/updated:', this.data);
  }

  /**
   * Get cookie value by name
   */
  getCookie(name) {
    const nameEQ = name + "=";
    const cookies = document.cookie.split(';');
    
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length);
      }
    }
    
    return null;
  }

  /**
   * Build UTM value string for form submission
   * @param {String} utm_source_field - Wufoo field for utm_source
   * @param {String} utm_medium_field - Wufoo field for utm_medium
   * @param {String} utm_campaign_field - Wufoo field for utm_campaign
   * @param {String} utm_term_field - Wufoo field for utm_term
   * @param {String} utm_content_field - Wufoo field for utm_content
   * @param {String} referring_page_field - Wufoo field for referring page
   * @returns {String} Query string with UTM values
   */
  static getUTMValueString(
    utm_source_field = 'Field1',
    utm_medium_field = 'Field2',
    utm_campaign_field = 'Field3',
    utm_term_field = 'Field4',
    utm_content_field = 'Field5',
    referring_page_field = 'Field7'
  ) {
    let utmData = null;
    const defaultValue = 'Organic/Referral';

    // Try to get UTM data from cookie (without creating new instance)
    try {
      const cookieValue = UTM.getCookieStatic('utm_cookie');
      if (cookieValue) {
        utmData = JSON.parse(decodeURIComponent(cookieValue));
      }
    } catch (error) {
      console.error('Error reading UTM cookie:', error);
    }

    // Helper function to get value with fallback and encoding
    const getValue = (key) => {
      if (utmData && utmData[key] && utmData[key] !== false) {
        return encodeURIComponent(utmData[key]);
      }
      return defaultValue;
    };

    const utm_source = getValue('utm_source');
    const utm_medium = getValue('utm_medium');
    const utm_campaign = getValue('utm_campaign');
    const utm_term = getValue('utm_term');
    const utm_content = getValue('utm_content');
    const referring_page = encodeURIComponent(location.host + location.pathname);

    // Build query string
    const params = [
      `${utm_source_field}=${utm_source}`,
      `${utm_medium_field}=${utm_medium}`,
      `${utm_campaign_field}=${utm_campaign}`,
      `${utm_term_field}=${utm_term}`,
      `${utm_content_field}=${utm_content}`,
      `${referring_page_field}=${referring_page}`
    ];

    return params.join('&');
  }

  /**
   * Static method to get cookie value by name (prevents recursion)
   * @param {String} name - Cookie name
   * @returns {String|null}
   */
  static getCookieStatic(name) {
    const nameEQ = name + "=";
    const cookies = document.cookie.split(';');
    
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length);
      }
    }
    
    return null;
  }

  /**
   * Check if any UTM parameters exist in the URL
   * @returns {Boolean} True if any UTM parameter is found
   */
  utmValuesExist() {
    const params = ['utm_source', 'utm_campaign', 'utm_medium', 'utm_term', 'utm_content'];
    let foundAny = false;

    params.forEach(param => {
      const value = this.url.searchParams.get(param);
      this.data[param] = value !== null ? value : false;
      
      if (value !== null) {
        foundAny = true;
      }
    });

    return foundAny;
  }

  /**
   * Debug logging
   */
  debug() {
    // Check for global debug flag
    if (typeof debug !== 'undefined' && debug) {
      console.groupCollapsed('%c UTM Tracker', 'color: #bada55; font-weight: bold;');
      console.log('URL:', this.url.href);
      console.log('UTM Data:', this.data);
      console.log('Cookie:', this.getCookie(this.cookieName));
      console.log('Forms Found:', this.elIframeForms ? this.elIframeForms.length : 0);
      console.groupEnd();
    }
  }

  /**
   * Clear UTM cookie (useful for testing)
   */
  clearCookie() {
    document.cookie = `${this.cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    console.log('UTM cookie cleared');
  }

  /**
   * Get current UTM data (for external use)
   * @returns {Object}
   */
  getUTMData() {
    const cookieValue = this.getCookie(this.cookieName);
    
    if (!cookieValue) {
      return null;
    }
    
    try {
      return JSON.parse(decodeURIComponent(cookieValue));
    } catch (error) {
      console.error('Error parsing UTM data:', error);
      return null;
    }
  }
}
